import React, { lazy, Suspense, useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter as Router, Route, Routes, useLocation, useParams } from "react-router-dom";
import { resolveShareLink, ResolvedLink } from "./utils/comparatorApi";
import { initGA, trackPageView } from "./utils/analytics";
import Home from "./pages/Home";
import SearchResults from "./components/SearchResults";
import "mapbox-gl/dist/mapbox-gl.css"; // Import Mapbox CSS globally
import YachtDetail from "./components/YachtDetail";
import DestinationDetail from "./components/DestinationDetail";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import GeneralContainer from "./components/general/GeneralContainer";
import ContactUs from "./components/general/ContactUs";
import FAQ from "./components/general/FAQ";
import SiteFooter from "./components/layout/SiteFooter";
import HelpPage from "./pages/HelpPage";
import ComparatorBar from "./components/comparator/ComparatorBar";
import SailingPlanPage from "./pages/SailingPlanPage";
import WhatsAppButton from "./components/WhatsAppButton";
import BetaBanner from "./components/BetaBanner";
import FeedbackButton from "./components/FeedbackButton";
import FeedbackModal from "./components/FeedbackModal";

const ComparePage = lazy(() => import("./components/comparator/ComparePage"));

const ShareRedirect: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const [resolved, setResolved] = useState<ResolvedLink | null>(null);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!code) return;
    resolveShareLink(code)
      .then((result) => {
        if (result.expired) {
          setExpired(true);
          return;
        }
        if (result.page_type === 'sailing_plan') {
          setResolved(result);
        } else {
          window.location.replace(result.target_url);
        }
      })
      .catch(() => { window.location.replace('/'); });
  }, [code]);

  if (expired) {
    return (
      <div style={{ padding: '4rem', textAlign: 'center', color: '#666' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚓</div>
        <div style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: '0.5rem' }}>This sailing plan has expired</div>
        <div style={{ fontSize: '0.9rem' }}>The trip has already set sail. <a href="/" style={{ color: '#1a1a2e' }}>Find your next adventure →</a></div>
      </div>
    );
  }
  if (resolved?.page_type === 'sailing_plan') {
    return <SailingPlanPage payload={resolved.payload} code={code!} />;
  }
  return <div style={{ padding: '4rem', textAlign: 'center' }}>Redirecting…</div>;
};

const GATracker: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    if (localStorage.getItem('cookieConsent') === 'accepted') {
      trackPageView(location.pathname);
    }
  }, [location]);
  return null;
};

const GlobalFooter: React.FC = () => {
  const location = useLocation();
  const isCompare = location.pathname === '/compare';
  return (
    <>
      {!isCompare && (
        <div style={{ padding: "60px 40px", background: "#ffffff" }}>
          <div className="elfsight-app-488a13f7-59c9-455c-b321-eac6ac2ea512" data-elfsight-app-lazy />
        </div>
      )}
      <SiteFooter />
      <ComparatorBar />
    </>
  );
};

function App() {
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('cookieConsent') === 'accepted') {
      initGA();
    }
  }, []);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <GATracker />
      <BetaBanner onFeedbackClick={() => setIsFeedbackOpen(true)} />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/yacht-details/:id" element={<Home />} />
        <Route path="/search-results" element={<SearchResults />} />
        <Route path="/yacht-details" element={<YachtDetail />} />{" "}
        {/* Yacht Details route */}
        <Route path="/destination/:name" element={<DestinationDetail />} />{" "}
        {/* Route for destination details */}
        <Route path="/general" element={<GeneralContainer />} />{" "}
        <Route path="/contact" element={<ContactUs />} />{" "}
        <Route path="/faq" element={<FAQ />} />{" "}
        <Route path="/help" element={<HelpPage />} />
        <Route path="/s/:code" element={<ShareRedirect />} />
        <Route path="/compare" element={
          <Suspense fallback={<div style={{ padding: "4rem", textAlign: "center" }}>Loading...</div>}>
            <ComparePage />
          </Suspense>
        } />
      </Routes>
      <GlobalFooter />
      <WhatsAppButton />
      <FeedbackButton onClick={() => setIsFeedbackOpen(true)} />
      <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
    </Router>
  );
}

export default App;
